import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Container,
} from '@mui/material';
import { Search, Article } from '@mui/icons-material';
import { resourcesAPI } from '../services/api';
import '../styles/public-pages/resources.css';

interface Resource {
  id: string;
  title: string;
  description: string;
  content: string;
  slug: string;
  featuredImage?: {
    url: string;
    alt: string;
  };
  category: string;
  tags: string[];
  publishedDate: string;
  updatedDate: string;
}

const Resources: React.FC = () => {
  const navigate = useNavigate();
  const [resources, setResources] = useState<Resource[]>([]);
  const [filteredResources, setFilteredResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  useEffect(() => {
    fetchResources();
  }, []);

  useEffect(() => {
    filterResources();
  }, [resources, searchTerm, selectedCategory]);

  const fetchResources = async () => {
    try {
      setLoading(true);
      const response = await resourcesAPI.getAll();
      if (response.data.success) {
        setResources(response.data.data);
      } else {
        setError('Failed to fetch resources');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch resources');
    } finally {
      setLoading(false);
    }
  };

  const filterResources = () => {
    let filtered = resources;

    if (searchTerm) {
      filtered = filtered.filter(
        (resource) =>
          resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          resource.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(resource => resource.category === selectedCategory);
    }

    setFilteredResources(filtered);
  };

  const handleResourceClick = (resource: Resource) => {
    navigate(`/resources/${resource.id}`);
  };

  const getUniqueCategories = () => {
    const categories = resources.map(resource => resource.category).filter(Boolean);
    return Array.from(new Set(categories));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box className="resources-loading" display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="resources-error" p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box className="resources-page" sx={{ bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box
        className="resources-hero"
        sx={{
          pt: { xs: 14, md: 20 },
          pb: { xs: 12, md: 16 },
          background:
            'radial-gradient(1200px 600px at 50% 15%, rgba(0,67,254,0.18) 0%, rgba(3,163,255,0.08) 45%, rgba(255,255,255,1) 75%)',
        }}
      >
        <Container className="resources-hero__container" maxWidth="lg">
          <Box className="resources-hero__content" sx={{ textAlign: 'center', maxWidth: 920, mx: 'auto' }}>
            <Typography
              className="resources-hero__label"
              component="span"
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                color: 'text.secondary',
                display: 'block',
                mb: 2,
              }}
            >
              Resources
            </Typography>
            <Typography
              className="resources-hero__title"
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.02em',
                fontSize: { xs: 36, sm: 44, md: 56 },
                lineHeight: 1.1,
              }}
            >
              Knowledge that drives results
            </Typography>
            <Typography
              className="resources-hero__subtitle"
              variant="h6"
              color="text.secondary"
              sx={{ mt: 2, maxWidth: 820, mx: 'auto' }}
            >
              Explore our collection of guides, templates, and insights to help you build better employee experiences and drive engagement across your organization.
            </Typography>
            <Box
              className="resources-hero__search"
              sx={{
                mt: 4,
                maxWidth: 640,
                mx: 'auto',
              }}
            >
              <TextField
                className="resources-search__input"
                fullWidth
                placeholder="Search resources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Resources Content */}
      <Box className="resources-content" id="resources-content" p={3}>
        {/* Search and Filter Controls */}
      <Box className="resources-search" mb={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <Box className="resources-search__filters" display="flex" gap={1} flexWrap="wrap">
              <Chip
                className="resources-search__filter-chip"
                label="All Categories"
                onClick={() => setSelectedCategory('')}
                color={selectedCategory === '' ? 'primary' : 'default'}
                variant={selectedCategory === '' ? 'filled' : 'outlined'}
              />
              {getUniqueCategories().map((category) => (
                <Chip
                  className="resources-search__filter-chip"
                  key={category}
                  label={category}
                  onClick={() => setSelectedCategory(category)}
                  color={selectedCategory === category ? 'primary' : 'default'}
                  variant={selectedCategory === category ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Resources Grid */}
      {filteredResources.length === 0 ? (
        <Box className="resources-empty" textAlign="center" py={4}>
          <Article className="resources-empty__icon" sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography className="resources-empty__title" variant="h6" color="text.secondary">
            No resources found
          </Typography>
          <Typography className="resources-empty__description" variant="body2" color="text.secondary">
            {searchTerm || selectedCategory
              ? 'Try adjusting your search or filter criteria'
              : 'Resources will appear here once they are added to Contentful'}
          </Typography>
        </Box>
      ) : (
        <Grid className="resources-grid" container spacing={3}>
          {filteredResources.map((resource) => (
            <Grid className="resources-grid__item" item xs={12} sm={6} md={3} key={resource.id}>
              <Card
                className="resources-card"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
                onClick={() => handleResourceClick(resource)}
              >
                {resource.featuredImage && (
                  <CardMedia
                    className="resources-card__media"
                    component="img"
                    height="200"
                    image={resource.featuredImage.url.startsWith('http') 
                      ? resource.featuredImage.url 
                      : `https:${resource.featuredImage.url}`}
                    alt={resource.featuredImage.alt}
                  />
                )}
                <CardContent className="resources-card__content" sx={{ flexGrow: 1 }}>
                  <Typography className="resources-card__title" variant="h6" component="h2" gutterBottom>
                    {resource.title}
                  </Typography>
                  <Typography className="resources-card__description" variant="body2" color="text.secondary" paragraph>
                    {resource.description}
                  </Typography>
                  <Box className="resources-card__meta" display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                    {resource.category && (
                      <Chip
                        className="resources-card__category"
                        label={resource.category}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    <Typography className="resources-card__date" variant="caption" color="text.secondary">
                      {formatDate(resource.publishedDate)}
                    </Typography>
                  </Box>
                  {resource.tags.length > 0 && (
                    <Box className="resources-card__tags" mt={1}>
                      {resource.tags.slice(0, 3).map((tag) => (
                        <Chip
                          className="resources-card__tag"
                          key={tag}
                          label={tag}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                      {resource.tags.length > 3 && (
                        <Typography variant="caption" color="text.secondary">
                          +{resource.tags.length - 3} more
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      </Box>
    </Box>
  );
};

export default Resources;
